import React, { useRef, useState } from "react";

/* ══════════════════════════════════════════════════════
   Block registry + compiler
   These MUST stay in sync with the HTML rendered by the
   email service — keep the markup identical to what real
   SendGrid emails produce, so the Preview pane is truthful.
   ══════════════════════════════════════════════════════ */

export const BLOCK_TYPES = [
  { type: "heading", label: "Heading",       hint: "Big title text" },
  { type: "text",    label: "Text",          hint: "A paragraph of body copy" },
  { type: "details", label: "Details table", hint: "Rows of label → value" },
  { type: "button",  label: "Button",        hint: "Call-to-action link" },
  { type: "info",    label: "Info box",      hint: "Callout with title + body" },
  { type: "wallet",  label: "Wallet panel",  hint: "Digital card QR + buttons" },
  { type: "divider", label: "Divider",       hint: "Thin horizontal line" },
  { type: "spacer",  label: "Spacer",        hint: "Vertical whitespace" },
  { type: "html",    label: "Custom HTML",   hint: "Escape hatch for raw HTML" },
];

const BLOCK_DEFAULTS = {
  heading: { level: "h1", text: "Your heading", color: "#1e293b" },
  text:    { html: "Your text here.", color: "#334155" },
  details: { rows: [{ label: "Label", value: "Value" }] },
  button:  { text: "Click here", url: "https://example.com", color: "#2563eb", text_color: "#ffffff" },
  info:    { title: "Note", body: "Body copy here.", background: "#f8fafc", border: "#e2e8f0" },
  wallet:  {},
  divider: {},
  spacer:  { height: 16 },
  html:    { html: "<!-- Your HTML -->" },
};

export function newBlock(type) {
  return { type, ...(JSON.parse(JSON.stringify(BLOCK_DEFAULTS[type] || {}))) };
}

/* ── Compile a single block to the exact HTML the email uses ── */
function blockToHtml(b) {
  switch (b.type) {
    case "heading": {
      const sizes = { h1: "26px", h2: "22px", h3: "18px" };
      const size = sizes[b.level] || "26px";
      return `<h1 style="margin:0 0 16px;font-size:${size};font-weight:700;color:${b.color || "#1e293b"};">${b.text || ""}</h1>`;
    }
    case "text":
      return `<div style="margin:0 0 20px;font-size:15px;color:${b.color || "#334155"};line-height:1.7;">${b.html || ""}</div>`;
    case "details": {
      const rows = (b.rows || []).map((r) => `
  <tr>
    <td style="padding:12px 16px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;width:40%;color:#64748b;font-size:14px;">${r.label}</td>
    <td style="padding:12px 16px;background:#fff;border:1px solid #e2e8f0;font-size:15px;color:#1e293b;font-weight:700;">${r.value}</td>
  </tr>`).join("");
      return `<table style="width:100%;border-collapse:collapse;margin:0 0 28px;">${rows}\n</table>`;
    }
    case "button": {
      const bg = b.color || "#2563eb";
      const fg = b.text_color || "#ffffff";
      return `<div style="text-align:center;margin:0 0 24px;"><a href="${b.url || "#"}" style="display:inline-block;padding:12px 28px;background:${bg};color:${fg};text-decoration:none;border-radius:8px;font-weight:600;font-size:15px;">${b.text || "Button"}</a></div>`;
    }
    case "info":
      return `<div style="background:${b.background || "#f8fafc"};border:1px solid ${b.border || "#e2e8f0"};border-radius:10px;padding:16px 20px;margin:0 0 20px;">${b.title ? `<strong style="display:block;margin-bottom:6px;color:#0f172a;">${b.title}</strong>` : ""}<span style="color:#64748b;font-size:14px;line-height:1.6;">${b.body || ""}</span></div>`;
    case "wallet":
      return "{{wallet_block}}";
    case "divider":
      return `<hr style="border:0;border-top:1px solid #e2e8f0;margin:24px 0;" />`;
    case "spacer":
      return `<div style="height:${b.height || 16}px;line-height:${b.height || 16}px;">&nbsp;</div>`;
    case "html":
      return b.html || "";
    default:
      return "";
  }
}

export function blocksToHtml(blocks) {
  return (blocks || []).map(blockToHtml).join("\n");
}

/* ══════════════════════════════════════════════════════
   UI icons
   ══════════════════════════════════════════════════════ */
const Icon = {
  up:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg>,
  down:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>,
  remove: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  plus:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
};

/* ══════════════════════════════════════════════════════
   Per-block editors
   ══════════════════════════════════════════════════════ */

function LabeledField({ label, children }) {
  return (
    <label className="be-field">
      <span className="be-field-label">{label}</span>
      {children}
    </label>
  );
}

function HeadingEditor({ block, onChange, registerInput }) {
  return (
    <div className="be-block-body">
      <div className="be-row">
        <LabeledField label="Size">
          <select value={block.level || "h1"} onChange={(e) => onChange({ ...block, level: e.target.value })}>
            <option value="h1">Large (H1)</option>
            <option value="h2">Medium (H2)</option>
            <option value="h3">Small (H3)</option>
          </select>
        </LabeledField>
        <LabeledField label="Color">
          <input type="color" value={block.color || "#1e293b"} onChange={(e) => onChange({ ...block, color: e.target.value })} />
        </LabeledField>
      </div>
      <LabeledField label="Text">
        <input
          ref={registerInput}
          type="text"
          value={block.text || ""}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          placeholder="Welcome, {{first_name}}!"
        />
      </LabeledField>
    </div>
  );
}

function TextEditor({ block, onChange, registerInput }) {
  return (
    <div className="be-block-body">
      <LabeledField label="Text (supports simple HTML like <strong>, <a>, <br />)">
        <textarea
          ref={registerInput}
          rows={3}
          value={block.html || ""}
          onChange={(e) => onChange({ ...block, html: e.target.value })}
          placeholder="Your membership at <strong>{{venue_name}}</strong> is now active."
        />
      </LabeledField>
      <LabeledField label="Text color">
        <input type="color" value={block.color || "#334155"} onChange={(e) => onChange({ ...block, color: e.target.value })} />
      </LabeledField>
    </div>
  );
}

function DetailsEditor({ block, onChange, registerInput }) {
  const rows = block.rows || [];
  const update = (i, patch) => {
    const next = rows.slice();
    next[i] = { ...next[i], ...patch };
    onChange({ ...block, rows: next });
  };
  const add = () => onChange({ ...block, rows: [...rows, { label: "Label", value: "Value" }] });
  const remove = (i) => onChange({ ...block, rows: rows.filter((_, j) => j !== i) });

  return (
    <div className="be-block-body">
      <div className="be-rows-grid">
        <div className="be-rows-head">
          <span>Label</span><span>Value</span><span />
        </div>
        {rows.map((row, i) => (
          <div key={i} className="be-rows-row">
            <input
              ref={i === 0 ? registerInput : undefined}
              type="text"
              value={row.label}
              onChange={(e) => update(i, { label: e.target.value })}
              placeholder="Membership ID"
            />
            <input
              type="text"
              value={row.value}
              onChange={(e) => update(i, { value: e.target.value })}
              placeholder="{{membership_id}}"
            />
            <button type="button" className="be-row-remove" onClick={() => remove(i)} title="Remove row">{Icon.remove}</button>
          </div>
        ))}
      </div>
      <button type="button" className="be-add-row" onClick={add}>{Icon.plus} Add row</button>
    </div>
  );
}

function ButtonEditor({ block, onChange, registerInput }) {
  return (
    <div className="be-block-body">
      <LabeledField label="Button label">
        <input
          ref={registerInput}
          type="text"
          value={block.text || ""}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          placeholder="Click here"
        />
      </LabeledField>
      <LabeledField label="Link URL">
        <input
          type="url"
          value={block.url || ""}
          onChange={(e) => onChange({ ...block, url: e.target.value })}
          placeholder="https://example.com"
        />
      </LabeledField>
      <div className="be-row">
        <LabeledField label="Button color">
          <input type="color" value={block.color || "#2563eb"} onChange={(e) => onChange({ ...block, color: e.target.value })} />
        </LabeledField>
        <LabeledField label="Text color">
          <input type="color" value={block.text_color || "#ffffff"} onChange={(e) => onChange({ ...block, text_color: e.target.value })} />
        </LabeledField>
      </div>
    </div>
  );
}

function InfoEditor({ block, onChange, registerInput }) {
  return (
    <div className="be-block-body">
      <LabeledField label="Title (optional)">
        <input
          ref={registerInput}
          type="text"
          value={block.title || ""}
          onChange={(e) => onChange({ ...block, title: e.target.value })}
          placeholder="Note"
        />
      </LabeledField>
      <LabeledField label="Body">
        <textarea
          rows={2}
          value={block.body || ""}
          onChange={(e) => onChange({ ...block, body: e.target.value })}
          placeholder="Information the member should see."
        />
      </LabeledField>
    </div>
  );
}

function SpacerEditor({ block, onChange }) {
  return (
    <div className="be-block-body">
      <LabeledField label={`Height — ${block.height || 16}px`}>
        <input
          type="range"
          min="4" max="60"
          value={block.height || 16}
          onChange={(e) => onChange({ ...block, height: Number(e.target.value) })}
        />
      </LabeledField>
    </div>
  );
}

function HtmlEditor({ block, onChange, registerInput }) {
  return (
    <div className="be-block-body">
      <LabeledField label="Raw HTML (power-user only)">
        <textarea
          ref={registerInput}
          rows={4}
          value={block.html || ""}
          onChange={(e) => onChange({ ...block, html: e.target.value })}
          spellCheck={false}
          className="mono"
        />
      </LabeledField>
    </div>
  );
}

function WalletNote() {
  return (
    <div className="be-block-body">
      <div className="be-wallet-note">
        Renders the member's Apple / Google Wallet buttons and a scannable
        QR code automatically. No editing needed — the panel only appears
        when Litecard is enabled for this venue.
      </div>
    </div>
  );
}

function DividerNote() {
  return (
    <div className="be-block-body">
      <div className="be-wallet-note">A thin horizontal rule. No options.</div>
    </div>
  );
}

const EDITORS = {
  heading: HeadingEditor,
  text:    TextEditor,
  details: DetailsEditor,
  button:  ButtonEditor,
  info:    InfoEditor,
  spacer:  SpacerEditor,
  html:    HtmlEditor,
  wallet:  WalletNote,
  divider: DividerNote,
};

/* ══════════════════════════════════════════════════════
   Add-block picker
   ══════════════════════════════════════════════════════ */

function AddBlockMenu({ onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="be-add-wrap">
      <button type="button" className="be-add-btn" onClick={() => setOpen((v) => !v)}>
        {Icon.plus} Add block
      </button>
      {open && (
        <div className="be-add-menu" onMouseLeave={() => setOpen(false)}>
          {BLOCK_TYPES.map((b) => (
            <button
              key={b.type}
              type="button"
              className="be-add-menu-item"
              onClick={() => { onAdd(b.type); setOpen(false); }}
            >
              <span className="be-add-menu-label">{b.label}</span>
              <span className="be-add-menu-hint">{b.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Main editor
   ══════════════════════════════════════════════════════ */

export default function EmailBlockEditor({ blocks, onChange, variables = [] }) {
  // Track which block has an active input so variable chips know where
  // to insert. Each block's editor registers its "primary" input via
  // registerInput; focusing the input sets activeIdx.
  const inputRefs = useRef({}); // { [blockIdx]: HTMLElement }
  const [activeIdx, setActiveIdx] = useState(null);

  const updateBlock = (idx, patch) => {
    const next = blocks.slice();
    next[idx] = typeof patch === "function" ? patch(next[idx]) : patch;
    onChange(next);
  };
  const removeBlock = (idx) => onChange(blocks.filter((_, i) => i !== idx));
  const moveBlock = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = blocks.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const addBlock = (type) => onChange([...blocks, newBlock(type)]);

  const insertVariable = (key) => {
    const token = `{{${key}}}`;
    if (activeIdx == null) return;
    const el = inputRefs.current[activeIdx];
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end   = el.selectionEnd   ?? el.value.length;
    const before = el.value.slice(0, start);
    const after  = el.value.slice(end);
    const nextValue = before + token + after;
    // Fire a native input event so React picks it up
    const setter = Object.getOwnPropertyDescriptor(
      el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype,
      "value"
    ).set;
    setter.call(el, nextValue);
    el.dispatchEvent(new Event("input", { bubbles: true }));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="be-root">
      {/* Variable chips — click to insert at cursor */}
      {variables.length > 0 && (
        <div className="be-vars">
          <span className="be-vars-label">Insert variable</span>
          <div className="be-vars-chips">
            {variables.map((v) => (
              <button
                key={v.key}
                type="button"
                className="be-chip"
                onClick={() => insertVariable(v.key)}
                disabled={activeIdx == null}
                title={activeIdx == null ? "Click into a text field first" : `${v.label} — e.g. ${v.example}`}
              >
                {`{{${v.key}}}`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Blocks */}
      <div className="be-blocks">
        {blocks.map((block, idx) => {
          const Editor = EDITORS[block.type] || HtmlEditor;
          const typeLabel = BLOCK_TYPES.find((t) => t.type === block.type)?.label || block.type;
          return (
            <div key={idx} className={`be-block ${activeIdx === idx ? "active" : ""}`}>
              <div className="be-block-header">
                <span className="be-block-tag">{typeLabel}</span>
                <div className="be-block-actions">
                  <button type="button" className="be-icon-btn" onClick={() => moveBlock(idx, -1)} disabled={idx === 0} title="Move up">{Icon.up}</button>
                  <button type="button" className="be-icon-btn" onClick={() => moveBlock(idx, +1)} disabled={idx === blocks.length - 1} title="Move down">{Icon.down}</button>
                  <button type="button" className="be-icon-btn danger" onClick={() => removeBlock(idx)} title="Remove block">{Icon.remove}</button>
                </div>
              </div>
              <div onFocusCapture={() => setActiveIdx(idx)}>
                <Editor
                  block={block}
                  onChange={(b) => updateBlock(idx, b)}
                  registerInput={(el) => { if (el) inputRefs.current[idx] = el; }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <AddBlockMenu onAdd={addBlock} />
    </div>
  );
}
